import React, { useState, useEffect } from 'react';
import { dataService } from '../../../../Services/data/data.service';
import { makeStyles } from "@material-ui/core/styles";
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, TextField } from '@mui/material';
import { getAuth } from "firebase/auth";
import { X, Check } from 'lucide-react';
import { crudTypes } from '../../../../Config/constants';
import Button from '../../../Shared/Buttons/Button';
import CustomCheckbox from '../../../Shared/Checkbox';

export default function CreateAccountCategoryDialog({ dialogOpen, closeDialog, accountCategoryToEdit, companies, ...props }) {
    const classes = useStyles();
    const auth = getAuth();

    const [generalError, setGeneralError] = useState(null);

    const [name, setName] = useState(null);
    const [nameError, setNameError] = useState(null);

    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedCompanyError, setSelectedCompanyError] = useState(null);

    const [isRevenue, setIsRevenue] = useState(true);
    const [createAnother, setCreateAnother] = useState(false);

    useEffect(() => {
        if (companies.length === 1) {
            setSelectedCompanyId(companies[0].id);
        }
    }, [companies])

    useEffect(() => {
        if (accountCategoryToEdit) {
            setName(accountCategoryToEdit.name);
            setSelectedCompanyId(accountCategoryToEdit.companyId);
            setIsRevenue(accountCategoryToEdit.isRevenue);
        }
    }, [accountCategoryToEdit])

    const changeName = (e) => {
        setName(e.target.value);
        if (e.target.value.length < 1) {
            setName(null);
        }
    }

    const validateFields = () => {
        var validationGood = true;
        if (!selectedCompanyId) {
            validationGood = false;
            setSelectedCompanyError("Please select a company")
        }
        if (name === "" || name === null || name === undefined) {
            validationGood = false;
            setNameError("Name is required!");
        } else if (name.length > 50) {
            setNameError("Name can't be over 50 characters")
        }

        return validationGood;
    }

    const handleSubmitAccountCategory = () => {
        resetErrors();
        if (validateFields()) {

            // Check if currently editing or creating a user
            if (accountCategoryToEdit) {
                var accountCategoryObject = {
                    id: accountCategoryToEdit.id,
                    name: name,
                    companyId: selectedCompanyId,
                    isRevenue: isRevenue,
                    updatedByUserId: props.userId,
                };
                // Update user
                dataService.updateAccountCategory(accountCategoryObject).then(response => {
                    if (response.success) {
                        toast.success(response.message, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 })
                        response.accountCategory.accounts = accountCategoryToEdit.accounts;
                        response.accountCategory.isActive = true;
                        props.onSuccess(response.accountCategory, crudTypes.UPDATE);
                        handleCloseDialog();
                    } else {
                        setGeneralError(response.message);
                    }
                })
            } else {
                var accountCategoryObject = {
                    name: name,
                    companyId: selectedCompanyId,
                    isRevenue: isRevenue,
                    insertedByUserId: props.userId,
                };
                // Create new user
                dataService.createAccountCategory(accountCategoryObject).then(response => {
                    if (response.success) {
                        toast.success(response.message, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 })
                        accountCategoryObject.id = response.accountCategoryId
                        accountCategoryObject.isActive = true
                        props.onSuccess(accountCategoryObject, crudTypes.CREATE);
                        if (createAnother) {
                            resetErrors();
                            setName(null);
                            setIsRevenue(true);
                        } else {
                            handleCloseDialog();
                        }
                    } else {
                        setGeneralError(response.message);
                    }
                }).catch((error) => {
                    console.log(error)
                });
            }
        }
    }

    const resetErrors = () => {
        setSelectedCompanyError(null);
        setNameError(null);
        setGeneralError(null);
    };

    const handleCloseDialog = () => {
        setSelectedCompanyId(null);
        setName(null);
        setIsRevenue(true);
        setCreateAnother(false);

        resetErrors();

        closeDialog();
    }

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            aria-labelledby="draggable-dialog-title"
            classes={{ paper: classes.dialogPaper }}
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {accountCategoryToEdit ? "Edit" : "Create"} account category
                </DialogTitle>
                <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    {generalError &&
                        <div className={classes.errorContainer}>
                            <p className={classes.errorMessage}>{generalError}</p>
                        </div>
                    }
                    <div className={classes.form}>
                        {companies.length > 1 &&
                            <div className={classes.inputContainer}>
                                <div className={classes.labelContainer}>
                                    <label className={classes.labelText}>Company*</label>
                                    <p className={classes.inputError}>{selectedCompanyError}</p>
                                </div>

                                <Select
                                    value={selectedCompanyId}
                                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                                    displayEmpty
                                    error={selectedCompanyError}
                                    style={{ width: "100%" }}
                                    className={classes.textField}
                                    disabled={accountCategoryToEdit}
                                >
                                    {/* If no company is selected, this is a placeholder */}
                                    {companies.length < 1 &&
                                        <MenuItem value="">
                                            <em>You are not part of a company</em>
                                        </MenuItem>
                                    }
                                    {/* Populate the dropdown with companies from allUserCompanies */}
                                    {companies.map((company, index) => (
                                        <MenuItem key={index} value={company.id}>
                                            {company.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        }
                        <div className={classes.inputContainer}>
                            <div className={classes.labelContainer}>
                                <label className={classes.labelText}>Name*</label>
                                <p className={classes.inputError}>{nameError}</p>
                            </div>
                            <TextField
                                className={classes.textField}
                                type={"text"}
                                variant="outlined"
                                size="small"
                                onChange={(event) => changeName(event)}
                                value={name}
                                fullWidth={true}
                                error={nameError !== null}
                                inputProps={{ maxLength: 50 }}
                            >
                            </TextField>
                        </div>
                        <div className={classes.inputContainer}>
                            <label className={classes.labelText}>Revenue/expense *</label>
                            <div className={classes.radioButtonContainer}>
                                <div className={classes.radioButton} onClick={() => setIsRevenue(true)}>
                                    <div className={classes.radio} style={{ backgroundColor: `${isRevenue ? "var(--color-primary)" : "transparent"}`, border: `${isRevenue ? "none" : "1px solid #0C0E29"}` }}>
                                        <Check className={classes.radioIcon} style={{ display: `${isRevenue ? "block" : "none"}` }} size={14} />
                                    </div>
                                    <p className={classes.radioButton__label}>Revenue</p>
                                </div>
                                <div className={classes.radioButton} onClick={() => setIsRevenue(false)}>
                                    <div className={classes.radio} style={{ backgroundColor: `${!isRevenue ? "var(--color-primary)" : "transparent"}`, border: `${!isRevenue ? "none" : "1px solid #0C0E29"}` }}>
                                        <Check className={classes.radioIcon} style={{ display: `${!isRevenue ? "block" : "none"}` }} size={14} />
                                    </div>
                                    <p className={classes.radioButton__label}>Expense</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '16px', paddingRight: '16px', paddingBottom: "16px" }}>
                {!accountCategoryToEdit ? (
                    <div className={classes.inputContainer} style={{ width: "fit-content" }}>
                        <CustomCheckbox checked={createAnother} onChange={setCreateAnother} label="Create another" />
                    </div>
                ) : <div />}
                <Button onClick={() => handleSubmitAccountCategory()}>{accountCategoryToEdit ? "Save" : "Create category"}</Button>
            </DialogActions>
        </Dialog >
    );
}


const useStyles = makeStyles(() => ({
    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '400px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: '#4654FB auto 1px',
        },
    },

    dialog__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        color: '#0C0E29',

        margin: '0px 0px 16px 0px',
    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "16px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    errorContainer: {
        width: "100%",
        backgroundColor: "#FDF3F3",

        border: "1px solid #FBE5E5",
        borderRadius: "6px",

        marginBottom: "16px",
        padding: "12px",
    },

    errorMessage: {
        fontSize: "16px",
        color: "#D84949"
    },

    form: {
        width: "100%",

        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },

    inputContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },

    labelContainer: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },

    inputError: {
        fontSize: "14px",
        color: "#D84949",

        margin: "0",
    },

    textField: {
        height: "40px",
    },

    radioButtonContainer: {
        width: "100%",
        display: "flex",
        gap: "8px",
    },

    radioButton: {
        width: "fit-content",
        height: "40px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px",

        borderRadius: "4px",

        transition: "all ease-in-out 0.2s",

        "&:hover": {
            backgroundColor: "#EDEDED",
            cursor: "pointer",
        }
    },

    radio: {
        width: "20px",
        height: "20px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        boxSizing: "border-box",

        borderRadius: "50%",
        border: "1px solid #0C0E29",
        backgroundColor: "transparent",
    },

    radioIcon: {
        color: "#F8F8F8",
    },

    radioButton__label: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },

    deleteButton: {
        height: "40px",
        padding: "0 16px",
        verticalAlign: 'center',
        border: "1px solid #D84949",
        borderRadius: "4px",

        fontSize: "16px",
        color: "#D84949",
        backgroundColor: "transparent",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#C73B3B",
            color: "white",
            border: "1px solid #C73B3B",
            cursor: "pointer",
        }
    }
}));
